import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Menubar } from 'primereact/menubar';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';

// Custom components
import DarkModeToggle from '../components/DarkModeToggle';
import BackToTopButton from '../components/BackToTopButton';
import CustomCursor from '../components/CustomCursor';

// Import CSS for new features
import '../assets/darkMode.css';
import '../assets/backToTop.css';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(false);
  const location = useLocation();
  const [currentPath, setCurrentPath] = useState<string>('/');

  // Update currentPath when location changes
  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location]);

  // Create menu items with active route styling
  // Create menu items with active route styling
  const createMenuItem = (
    path: string | null,
    label: string,
    icon: string,
    items?: any[]
  ) => {
    const isActive =
      (path && currentPath === path) ||
      (path && path !== '/' && currentPath.startsWith(path)) ||
      (items && items.some(item => item.path === currentPath));

    return {
      label,
      icon: `pi pi-fw ${icon}`,
      className: isActive ? 'active-route' : '',
      ...(path
        ? { command: () => (window.location.href = path) }
        : {}), // 👈 don't assign command if no path
      items: items?.map(item => ({
        label: item.label,
        icon: `pi pi-fw ${item.icon}`,
        className: currentPath === item.path ? 'active-route' : '',
        command: () => (window.location.href = item.path),
      })),
    };
  };


  const menuItems = [
    createMenuItem('/', 'Home', 'pi-home'),
    createMenuItem('/about', 'About Us', 'pi-info-circle'),
    // createMenuItem('/services', 'Services', 'pi-cog', [
    //   { path: '/services', label: 'Data Annotation', icon: 'pi-tags' },
    //   { path: '/services', label: 'AI/ML Training', icon: 'pi-book' },
    //   { path: '/services', label: 'Custom Analytics', icon: 'pi-chart-bar' },
    //   { path: '/services', label: 'Smart Surveillance', icon: 'pi-camera' },
    //   { path: '/services', label: 'AI Expertise on Demand', icon: 'pi-users' },
    //   { path: '/services', label: 'Object Detection', icon: 'pi-eye' },
    // ]),
    createMenuItem(null, 'Solutions', 'pi-briefcase', [
      { path: '/xconnected-ai', label: 'XConnected AI', icon: 'pi-car' },
      { path: '/vms', label: 'Video Management System', icon: 'pi-home' },
      { path: '/CrowdManagementSolution', label: 'Crowd Management Solution', icon: 'pi-shield' },
      { path: '/ems', label: 'Enterprise Monitoring System', icon: 'pi-shield' },
      { path: '/itms', label: 'Integrated Traffic Management Solution', icon: 'pi-car' },
      { path: '/TrafficHotspots', label: 'Traffic Hotspots & Incident Monitoring', icon: 'pi-shield' },
      { path: '/nms', label: 'Network Management System', icon: 'pi-sitemap' },
      { path: '/qsr', label: 'QSR', icon: 'pi-cog' },
      // { Traffic Hotspots & Incident Monitoringpath: '/ai-ml-training', label: 'AI/ML Training', icon: 'pi-verified' }
    ]),
    createMenuItem(null, 'Latest Launches', 'pi-sparkles', [
      {
        path: '/AutomatedInvoiceProcessingSystem',
        label: 'Automated Invoice Processing System',
        icon: 'pi-file',
      },
      {
        path: '/public-announcement-system',
        label: 'Public Announcement System',
        icon: 'pi-volume-up',
      },
      {
        path: '/multilingual-anpr-solution',
        label: 'Multilingual ANPR',
        icon: 'pi-car',
      },
      { path: '/nms', label: 'GenAI', icon: 'pi-sparkles' },
      { path: '/mlff', label: 'MLFF', icon: 'pi-building' },
      { path: '/blockchain', label: 'Blockchain', icon: 'pi-shield' },
    ]),
    // ✅ New Industries menu with dropdown options
    createMenuItem(null, 'Industries', 'pi-briefcase', [
      { path: '/smart-city', label: 'Smart City', icon: 'pi-building' },
      { path: '/smart-traffic', label: 'Smart Traffic Management', icon: 'pi-car' },
      { path: '/smart-factory', label: 'Smart Factory', icon: 'pi-cog' },
      { path: '/smart-restaurant', label: 'Smart Restaurant', icon: 'pi-car' },
      { path: '/real-estate', label: 'Real Estate', icon: 'pi-home' },
      { path: '/premise-safety', label: 'Premise Safety', icon: 'pi-shield' },
      { path: '/defense', label: 'Defense', icon: 'pi-verified' },
      { path: '/agriculture', label: 'Agriculture', icon: 'pi-home' },
    ]),
    createMenuItem(null, 'Portfolio', 'pi-briefcase', [
      { path: '/csr', label: 'CSR', icon: 'pi-heart' },
      { path: '/download', label: 'Download', icon: 'pi-download' },
      // { path: '/case/gallery', label: 'Gallery', icon: 'pi-cog' },
      // { path: '/case/services', label: 'Services', icon: 'pi-cog' },

    ]),
    // createMenuItem('/blog', 'Blog', 'pi-sitemap'),
    // createMenuItem('/download', 'Download', 'pi-download'),
    // createMenuItem('/csr', 'CSR', 'pi-heart'),
    createMenuItem('/contact', 'Contact Us', 'pi-envelope'),
  ];

  const start = <div className="logo">
    <NavLink to="/" className="flex align-items-center">
      <div className="flex align-items-center" style={{
        position: 'relative',
        filter: 'drop-shadow(0 0 8px rgba(46, 204, 113, 0.6))'
      }}>
        <img src={require('../assets/images/bitlens-logo.svg').default} alt="BitLens Logo" className="mr-2" style={{ height: '40px' }} />
      </div>
    </NavLink>
  </div>;

  const end = (
    <div className="flex align-items-center">
      <Button
        label="Contact Us"
        className="hidden md:flex neo-brutalism get-started-btn"
        style={{
          fontFamily: 'var(--font-subheading)',
          fontWeight: 600,
          letterSpacing: '0.05em',
          border: '2px solid #000',
          background: 'var(--accent-yellow)',
          color: '#000'
        }}
        onClick={() => window.location.href = '/contact'}
      />
      <Button
        icon="pi pi-bars"
        onClick={() => setSidebarVisible(true)}
        className="ml-2 md:hidden"
        rounded
        text
      />
    </div>
  );

  return (
    <div className="min-h-screen flex flex-column">
      {/* Custom cursor */}
      <CustomCursor />

      <div className="sticky top-0 z-5 shadow-2">
        <Menubar model={menuItems} start={start} className="border-none" />
      </div>

      {/* <Sidebar
        visible={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
        className="w-18rem"
      >
        <div className="p-4">
          <img src={require('../assets/images/bitlens-logo.svg').default} alt="BitLens Logo" style={{ height: '40px' }} />
        </div>
        <div className="flex flex-column gap-2 p-3">
          <NavLink 
            to="/" 
            className={({isActive}) => 
              `no-underline p-3 border-round ${isActive ? 'active-route' : 'text-color hover:text-primary'}`
            }
          >
            <i className="pi pi-home mr-3"></i>
            <span>Home</span>
          </NavLink>
          <NavLink 
            to="/about" 
            className={({isActive}) => 
              `no-underline p-3 border-round ${isActive ? 'active-route' : 'text-color hover:text-primary'}`
            }
          >
            <i className="pi pi-info-circle mr-3"></i>
            <span>About</span>
          </NavLink>
          <NavLink 
            to="/services" 
            className={({isActive}) => 
              `no-underline p-3 border-round ${isActive ? 'active-route' : 'text-color hover:text-primary'}`
            }
          >
            <i className="pi pi-cog mr-3"></i>
            <span>Services</span>
          </NavLink>

          <NavLink 
            to="/xconnected-ai" 
            className={({isActive}) => 
              `no-underline p-3 border-round ${isActive ? 'active-route' : 'text-color hover:text-primary'}`
            }
          >
            <i className="pi pi-sitemap mr-3"></i>
            <span>XConnectedAI</span>
          </NavLink>
          <NavLink 
            to="/download" 
            className={({isActive}) => 
              `no-underline p-3 border-round ${isActive ? 'active-route' : 'text-color hover:text-primary'}`
            }
          >
            <i className="pi pi-download mr-3"></i>
            <span>Download</span>
          </NavLink>
          <NavLink 
            to="/csr" 
            className={({isActive}) => 
              `no-underline p-3 border-round ${isActive ? 'active-route' : 'text-color hover:text-primary'}`
            }
          >
            <i className="pi pi-heart mr-3"></i>
            <span>CSR</span>
          </NavLink>
          <NavLink 
            to="/contact" 
            className={({isActive}) => 
              `no-underline p-3 border-round ${isActive ? 'active-route' : 'text-color hover:text-primary'}`
            }
          >
            <i className="pi pi-envelope mr-3"></i>
            <span>Contact</span>
          </NavLink>
        </div>
      </Sidebar> */}

      <main className="flex-grow-1">
        {children}
      </main>

      <footer className="bg-white text-gray-700 p-4">
        <div className="container mx-auto">
          <div className="grid">
            <div className="col-12 md:col-4">
              <div style={{
                position: 'relative',
                display: 'inline-block',
                filter: 'drop-shadow(0 0 8px rgba(46, 204, 113, 0.6))'
              }}>
                <img src={require('../assets/images/bitlens-logo.svg').default} alt="BitLens Logo" style={{ height: '40px', marginBottom: '10px' }} />
              </div>
              <p className="mb-3 subheading" style={{ color: '#707070', letterSpacing: '0.05em' }}>AI-powered video analytics solutions</p>
              <div className="flex gap-2 mt-3">
                <a href="https://www.facebook.com/" target="_blank" rel="noreferrer" className="p-2">
                  <i className="pi pi-facebook" style={{ color: '#707070' }}></i>
                </a>
                <a href="https://www.instagram.com/" target="_blank" rel="noreferrer" className="p-2">
                  <i className="pi pi-instagram" style={{ color: '#707070' }}></i>
                </a>
                <a href="https://www.linkedin.com/" target="_blank" rel="noreferrer" className="p-2">
                  <i className="pi pi-linkedin" style={{ color: '#707070' }}></i>
                </a>
                <a href="https://www.youtube.com/" target="_blank" rel="noreferrer" className="p-2">
                  <i className="pi pi-youtube" style={{ color: '#707070' }}></i>
                </a>
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="grid">
                <div className="col-6">
                  <h4 className="mb-4">Home</h4>
                  <ul className="p-0 m-0 list-none">
                    <li className="mb-2">
                      <a href="/" className="no-underline hover:text-primary animated-underline" style={{ color: '#707070' }}>
                        Home
                      </a>
                    </li>
                    <li className="mb-2">
                      <a href="/about" className="no-underline hover:text-primary animated-underline" style={{ color: '#707070' }}>
                        About Us
                      </a>
                    </li>
                    {/* <li className="mb-2">
                      <a href="/services" className="no-underline hover:text-primary animated-underline" style={{ color: '#707070' }}>
                        Services
                      </a>
                    </li> */}
                    <li className="mb-2">
                      <a href="/xconnected-ai" className="no-underline hover:text-primary animated-underline" style={{ color: '#707070' }}>
                        XconnectedAI
                      </a>
                    </li>
                    <li className="mb-2">
                      <a href="/download" className="no-underline hover:text-primary animated-underline" style={{ color: '#707070' }}>
                        Download
                      </a>
                    </li>
                    <li className="mb-2">
                      <a href="/csr" className="no-underline hover:text-primary animated-underline" style={{ color: '#707070' }}>
                        CSR
                      </a>
                    </li>
                    <li className="mb-2">
                      <a href="/contact" className="no-underline hover:text-primary animated-underline" style={{ color: '#707070' }}>
                        Contact
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="col-6">
                  <h4 className="mb-4">Solutions</h4>
                  <ul className="p-0 m-0 list-none">


                    <li className="mb-2">
                      <a href="/mlff" className="no-underline hover:text-primary" style={{ color: '#707070' }}>
                        MLFF
                      </a>
                    </li>
                    {/* <li className="mb-2">
                      <a href="/qsr" className="no-underline hover:text-primary" style={{ color: '#707070' }}>
                        QSR
                      </a>
                    </li> */}
                    <li className="mb-2">
                      <a href="/itms" className="no-underline hover:text-primary" style={{ color: '#707070' }}>
                        ITMS
                      </a>
                    </li>
                    <li className="mb-2">
                      <a href="/vms" className="no-underline hover:text-primary" style={{ color: '#707070' }}>
                        VMS
                      </a>
                    </li>
                    <li className="mb-2">
                      <a href="/nms" className="no-underline hover:text-primary" style={{ color: '#707070' }}>
                        Gen AI
                      </a>
                    </li>
                    <li className="mb-2">
                      <a href="/ems" className="no-underline hover:text-primary" style={{ color: '#707070' }}>
                        EMS
                      </a>
                    </li>
                    {/* <li className="mb-2">
                      <a href="/nms" className="no-underline hover:text-primary" style={{ color: '#707070' }}>
                        Gen AI
                      </a>
                    </li> */}
                    <li className="mb-2">
                      <a href="/CrowdManagementSolution" className="no-underline hover:text-primary" style={{ color: '#707070' }}>
                        Crowd Management Solution
                      </a>
                    </li>
                    {/* <li className="mb-2">
                      <a href="/blockchain" className="no-underline hover:text-primary" style={{ color: '#707070' }}>
                        Blockchain
                      </a>
                    </li> */}
                    {/* <li className="mb-2">
                      <a href="/TrafficHotspots" className="no-underline hover:text-primary" style={{ color: '#707070' }}>
                        Traffic Hotspots & Incident Monitoring
                      </a>
                    </li> */}
                    {/* <li className="mb-2">
                      <a href="/ai-ml-training" className="no-underline hover:text-primary" style={{ color: '#707070' }}>
                        AI/ML Training
                      </a>
                    </li> */}
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-12 md:col-4">
              <h4 className="mb-4">Contact us</h4>
              <p className="mb-3 line-height-3" style={{ color: '#707070' }}>Contact us now to schedule a demonstration or to learn more about our suite of security solutions.</p>
              <ul className="p-0 m-0 list-none">
                <li className="mb-2">
                  <a href="tel:+91-810-368-2776" className="no-underline hover:text-primary flex align-items-center" style={{ color: '#707070' }}>
                    <i className="pi pi-phone mr-2"></i>
                    <span>+91-810-368-2776</span>
                  </a>
                </li>
                <li className="mb-2">
                  <a href="tel:+1-425-410-8541" className="no-underline hover:text-primary flex align-items-center" style={{ color: '#707070' }}>
                    <i className="pi pi-phone mr-2"></i>
                    <span>+1-425-410-8541</span>
                  </a>
                </li>
                <li className="mb-2">
                  <a href="mailto:ai@bitlens.in" className="no-underline hover:text-primary flex align-items-center" style={{ color: '#707070' }}>
                    <i className="pi pi-envelope mr-2"></i>
                    <span>ai@bitlens.in</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-4 pt-4 border-top-1 border-300 text-center">
            <p className="text-sm" style={{ color: '#707070' }}>
              © 2017 BitLens Solutions. All rights reserved.
              <span className="y2k-gradient" style={{
                padding: '0.2rem 0.5rem',
                marginLeft: '0.5rem',
                fontFamily: 'var(--font-subheading)',
                fontWeight: 700,
                color: 'white',
                textShadow: '0 0 5px rgba(0,210,255,0.8), 0 0 10px rgba(0,210,255,0.5)',
                borderRadius: '4px'
              }}>
                ANALYZING TOMORROW
              </span>
            </p>
          </div>
        </div>
      </footer>

      {/* Custom back to top button */}
      <BackToTopButton />

      {/* Dark mode toggle positioned at bottom left */}
      <div className="dark-mode-toggle-container">
        <DarkModeToggle />
      </div>
    </div>
  );
};

export default MainLayout;
